import { API_BASE_URL } from '@/config/api-url';

export interface CaptureLeadPayload {
  email: string;
  quizId?: string;
  paywallId?: string;
  sessionId?: string;
  /**
   * WEB-1480 — решение посетителя о согласии на маркетинговые письма (кука
   * `w2a_consent`). То же необязательное поле, что аналитика везёт в
   * trackEvent.ts: одна отправка формы уходит двумя путями, и до этой
   * карточки решение знал только путь аналитики. Вне ЕС сервер по стране
   * подразумевает согласие, поэтому нажавший «Отклонить» получал письмо от
   * лид-пути. Решает по-прежнему сервер (resolveConsent) — это сигнал.
   * Решения нет → поля нет вовсе (незнакомое/лишнее поле при
   * forbidNonWhitelisted отбивает весь запрос).
   */
  consent?: 'granted' | 'denied';
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

/**
 * WEB-1716: чем закончилась запись лида. Раньше функция возвращала void и
 * глушила любую ошибку пустым перехватом — потерянный контакт не оставлял
 * следа нигде, владелец воронки не мог даже узнать, что терял.
 */
export interface LeadCaptureResult {
  /** Сервер принял лид (2xx). */
  ok: boolean;
  /** HTTP-код последней попытки; нет — запрос не дошёл вовсе (сеть). */
  status?: number;
  /** Сколько раз стучались (1 = с первого раза). */
  attempts: number;
}

/**
 * Паузы перед повторами (мс). Длина массива = число ПОВТОРОВ сверх первой
 * попытки. Держим короткими: покупатель в это время уже идёт по воронке
 * дальше, а вкладку может закрыть в любой момент.
 */
export const LEAD_CAPTURE_RETRY_DELAYS_MS = [400, 1200] as const;

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Временный отказ (стоит повторить) против плохого запроса (не стоит). */
function isRetriableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

/**
 * Lead capture for public quiz/paywall pages.
 * Uses bare fetch (no axios interceptor) so the public render bundle stays
 * thin and unauthenticated.
 *
 * WEB-1716 (деньги владельца воронки): наружу по-прежнему НЕ бросаем — поток
 * покупателя ломать нельзя, — но потеря перестала быть невидимой:
 *  1) сетевой сбой / 5xx / 429 / 408 повторяются с короткими паузами;
 *  2) итог возвращается вызывающему (`ok`), чтобы рантайм мог решить, что
 *     делать (квиз-визард по !ok не помечает почту отправленной и пробует
 *     ещё раз на следующем продвижении);
 *  3) окончательный провал уезжает в Sentry с контекстом воронки — тот же
 *     приём, что в store/projectsSlice.ts для проглоченной ошибки.
 * PII в отчёт не кладём: почта покупателя в Sentry не уходит.
 */
/**
 * WEB-2015 — как добыть отчётчик телеметрии. Отдельным параметром, а не
 * статическим импортом: `@sentry/react` весит десятки килобайт и по замыслу
 * WEB-1988 грузится в простое браузера ПОСЛЕ первого экрана — тем же
 * `scheduleOnIdle` (`publicBootstrap.ts`), которым публичный вход откладывает
 * `initPublicTracing()` (`config/sentryPublicTracing.ts`, WEB-2178: не
 * кабинетную настройку с записью экрана). Здесь тот же приём и та же причина
 * — плюс тесты получают подмену без мока модуля.
 */
export type LeadTelemetryLoader = () => Promise<
  Pick<typeof import('@sentry/react'), 'captureException'>
>;

const loadLeadTelemetry: LeadTelemetryLoader = () => import('@sentry/react');

export async function captureLeadPublic(
  projectId: string,
  payload: CaptureLeadPayload,
  loadTelemetry: LeadTelemetryLoader = loadLeadTelemetry,
): Promise<LeadCaptureResult> {
  const maxAttempts = LEAD_CAPTURE_RETRY_DELAYS_MS.length + 1;
  let lastStatus: number | undefined;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    let retriable = false;
    try {
      const res = await fetch(`${API_BASE_URL}/public/leads/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
      lastStatus = res.status;
      lastError = undefined;
      if (res.ok) return { ok: true, status: res.status, attempts: attempt };
      retriable = isRetriableStatus(res.status);
    } catch (error) {
      // Запрос не дошёл вовсе (сеть/офлайн) — статуса нет, повторяем.
      lastStatus = undefined;
      lastError = error;
      retriable = true;
    }

    const delay = LEAD_CAPTURE_RETRY_DELAYS_MS[attempt - 1];
    if (!retriable || delay === undefined) {
      void reportLeadCaptureFailure(
        loadTelemetry,
        projectId,
        payload,
        lastStatus,
        attempt,
        lastError,
      );
      return {
        ok: false,
        ...(lastStatus !== undefined && { status: lastStatus }),
        attempts: attempt,
      };
    }
    await wait(delay);
  }

  // Недостижимо (цикл всегда выходит через return), но контракт закрываем.
  void reportLeadCaptureFailure(
    loadTelemetry,
    projectId,
    payload,
    lastStatus,
    maxAttempts,
    lastError,
  );
  return {
    ok: false,
    ...(lastStatus !== undefined && { status: lastStatus }),
    attempts: maxAttempts,
  };
}

/**
 * Отчёт о провале. Отпускаем его ВДОГОНКУ (`void`), ответ вызывающему не
 * ждёт его завершения.
 *
 * Свежий взгляд пачки 31, п.4: раньше здесь стоял `await`. Отчётчик
 * добывается лениво — `@sentry/react` приезжает отдельным чанком (WEB-2015),
 * и на плохой сети эта загрузка висит десятки секунд. Всё это время
 * вызывающий не получал ответа, а квизовый визард взводит второй рубеж
 * WEB-1716 (`pendingLeadRef`) именно в `.then` этого обещания: пока оно
 * висело, повтора записи лида не было — контакт терялся ровно в том
 * сценарии, ради которого рубеж заводили. Порядок «сначала след, потом
 * ответ» держать не на чем: след живёт во внешней системе, а рубеж — у нас.
 *
 * Ни загрузка чанка, ни сам отчёт не имеют права сломать поток покупателя:
 * любая их ошибка глушится здесь, поэтому и отвязанное обещание не может
 * стать необработанным отказом.
 */
async function reportLeadCaptureFailure(
  loadTelemetry: LeadTelemetryLoader,
  projectId: string,
  payload: CaptureLeadPayload,
  status: number | undefined,
  attempts: number,
  error: unknown,
): Promise<void> {
  try {
    const telemetry = await loadTelemetry();
    telemetry.captureException(
      error instanceof Error
        ? error
        : new Error(
            `Lead capture failed (${status ?? 'no response'}) after ${attempts} attempt(s)`,
          ),
      {
        // Почты здесь намеренно нет — это PII.
        extra: {
          projectId,
          quizId: payload.quizId,
          paywallId: payload.paywallId,
          sessionId: payload.sessionId,
          status,
          attempts,
        },
        tags: { area: 'lead-capture' },
      },
    );
  } catch {
    /* отчёт об ошибке сам ломать поток покупателя не имеет права */
  }
}

export interface LeadResponse {
  id: string;
  projectId: string;
  email: string;
  quizId: string | null;
  paywallId: string | null;
  sessionId: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  createdAt: string;
  updatedAt: string;
  lastSeenAt: string;
}

export interface LeadListResponse {
  leads: LeadResponse[];
  total: number;
  page: number;
  limit: number;
  /**
   * WEB-703 (добор): выдача сужена до лидов текущего рядового участника — он
   * видит только лиды со СВОИХ квизов и пейволлов, а заявки без привязки к
   * ресурсу («сироты») не видит вовсе (решение владельца 02.09: сироты
   * остаются у владельца проекта и администратора). До этого поля участник и
   * владелец открывали один экран, видели разные «всего лидов» и оба считали
   * своё число полным.
   *
   * true бывает только у роли «участник»; у владельца, администратора и
   * менеджера по удержанию — false. Необязательное намеренно: старая вкладка
   * против нового сервера (и наоборот) просто не рисует пометку.
   */
  scoped?: boolean;
}

export interface ListLeadsParams {
  page?: number;
  limit?: number;
  search?: string;
}

/**
 * WEB-2015 — КАБИНЕТНАЯ половина файла: список лидов в разделе «Лиды».
 *
 * Клиент кабинета (`api/client.ts`) добывается ленивым импортом, а не
 * статическим. Причина не в кабинете, а в соседе по файлу: `captureLeadPublic`
 * зовёт публичный плеер, и статический импорт клиента тянул покупателю воронки
 * весь кабинетный обвяз — axios (139 КБ несжатых) плюс словари ошибок кабинета
 * и админки (130 КБ), которые покупателю не показываются никогда. Кабинет
 * ничего не теряет: вызов и так асинхронный, `await` у потребителей на месте.
 */
const cabinetLeads = () => import('@/api/leadsCabinet');

export const leadsApi = {
  list: async (projectId: string, params: ListLeadsParams = {}) =>
    (await cabinetLeads()).listLeads(projectId, params),
  /** WEB-2679: фоновая выгрузка лидов (тело — в `leadsCabinet.ts`). */
  startExport: async (projectId: string, search?: string) =>
    (await cabinetLeads()).startLeadsExport(projectId, search),
  exportStatus: async (projectId: string, jobId: string) =>
    (await cabinetLeads()).leadsExportStatus(projectId, jobId),
  /** Готовый файл: только тот, кто запросил, пока не вышел срок ссылки. */
  downloadExport: async (projectId: string, jobId: string) =>
    (await cabinetLeads()).downloadLeadsExport(projectId, jobId),
};
